// ============================================================================
// Component-scoped latest Activity owner for record lists
// ============================================================================
//
// CattleHerdsView's optional Last Activity column/sort once issued an effect
// fanout directly from the presenter. This owner keeps the same one-RPC-per-
// record contract, but gives each request a full authenticated Query key and
// lets Query dedupe, cancel, and fence it. It deliberately calls the existing
// Activity transport instead of introducing a second physical RPC site: the
// API layer remains the one owner of `list_activity_events` itself.
//
// This is not route data. A shared list sidecar cannot make a route permanent,
// boot-independent, or catalog-owned; the finite consumer registry is its
// receipt of the mounts it actually serves.
// ============================================================================

import {skipToken, useQuery} from '@tanstack/react-query';
import {useEffect, useMemo, useRef} from 'react';

import type {AuthState} from '../contexts/AuthContext.jsx';
import {listActivityEvents} from '../lib/activityApi.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {resourceKey, scopeOrNull, UNRESOLVED_RESOURCE_KEY} from '../app/data/routeDataCatalog.js';
import type {AuthenticatedScope} from '../app/data/routeDataCatalog.js';
import {RECORD_ACTIVITY_LATEST_RESOURCE} from './recordActivityLatestRegistry.js';

export {RECORD_ACTIVITY_LATEST_RESOURCE};

export const RECORD_ACTIVITY_LATEST_LIMIT = 1;
// This preserves the old herd-presenter fanout rather than making a hidden
// column open an unbounded burst of Activity RPCs on a large cattle list.
export const RECORD_ACTIVITY_LATEST_CONCURRENCY = 8;

export type LatestActivityValue = string | null;

export type RecordActivityLatestProjection = {
  readonly byRecordId: Readonly<Record<string, LatestActivityValue>>;
  readonly recordIds: readonly string[];
  readonly recordListKey: string;
  /** True only after every requested record has finished this fetch cycle. */
  readonly settled: boolean;
  /** A visible spinner is only needed while a record has no settled value yet. */
  readonly loading: boolean;
  readonly failedRecordIds: readonly string[];
  /** A whole-list failure stays distinct from ordinary per-record failures. */
  readonly error: unknown;
  /** Changes for each settled Query response, even when the timestamp repeats. */
  readonly settlementKey: string;
};

export type RecordActivityLatestLoad = {
  readonly byRecordId: Readonly<Record<string, LatestActivityValue>>;
  readonly failedRecordIds: readonly string[];
};

type LatestActivityQueryLike = {
  readonly data: RecordActivityLatestLoad | undefined;
  readonly isPending: boolean;
  readonly isFetching: boolean;
  readonly error: unknown;
  readonly dataUpdatedAt?: number;
  readonly errorUpdatedAt?: number;
};

export type RecordActivityLatestLoader = (
  sb: SupabaseClient,
  entityType: string,
  recordId: string,
  signal: AbortSignal,
  limit: number,
) => Promise<LatestActivityValue>;

/** Normalize the list selector once: blank/non-string IDs never become keys. */
export function collectRecordActivityLatestIds(recordIds: readonly unknown[] | null | undefined): readonly string[] {
  const unique = new Set<string>();
  for (const rawId of recordIds || []) {
    if (typeof rawId !== 'string') continue;
    const id = rawId.trim();
    if (id) unique.add(id);
  }
  return [...unique].sort();
}

/** Activity is private: public and unresolved identities create no observers. */
export function recordActivityLatestScope(authState: AuthState, rolePreview?: unknown): AuthenticatedScope | null {
  const scope = scopeOrNull(authState, rolePreview);
  return scope && scope.kind === 'authenticated' ? scope : null;
}

/** One list key carries scope, entity identity, selected records, and the one-row limit. */
export function recordActivityLatestQueryKey(
  scope: AuthenticatedScope,
  {
    entityType,
    recordIds,
    limit = RECORD_ACTIVITY_LATEST_LIMIT,
  }: {
    entityType: string;
    recordIds: readonly unknown[] | null | undefined;
    limit?: number;
  },
) {
  return resourceKey(RECORD_ACTIVITY_LATEST_RESOURCE.LATEST, scope, {
    entityType,
    recordIds: collectRecordActivityLatestIds(recordIds),
    limit,
  });
}

/** Read just the newest timestamp while keeping the Activity API as transport owner. */
export async function loadRecordActivityLatest(
  sb: SupabaseClient,
  entityType: string,
  recordId: string,
  signal: AbortSignal,
  limit: number = RECORD_ACTIVITY_LATEST_LIMIT,
): Promise<LatestActivityValue> {
  const rows = await listActivityEvents(sb, entityType, recordId, {limit, signal});
  const first = Array.isArray(rows) ? rows[0] : null;
  const createdAt = first && typeof first === 'object' ? (first as {created_at?: unknown}).created_at : null;
  return typeof createdAt === 'string' && createdAt ? createdAt : null;
}

function throwIfAborted(signal: AbortSignal): void {
  if (!signal.aborted) return;
  const error = new Error('Record Activity latest load was aborted.');
  error.name = 'AbortError';
  throw error;
}

/**
 * Preserve the herd presenter's eight-at-a-time automatic fanout in one
 * Query-owned list request. Individual RPC failures remain partial so their
 * rows sort last; only cancellation or an owner-level fault rejects Query.
 */
export async function loadRecordActivityLatestList(
  sb: SupabaseClient,
  entityType: string,
  recordIds: readonly unknown[] | null | undefined,
  signal: AbortSignal,
  {
    limit = RECORD_ACTIVITY_LATEST_LIMIT,
    concurrency = RECORD_ACTIVITY_LATEST_CONCURRENCY,
  }: {limit?: number; concurrency?: number} = {},
  loadLatest: RecordActivityLatestLoader = loadRecordActivityLatest,
): Promise<RecordActivityLatestLoad> {
  const normalizedRecordIds = collectRecordActivityLatestIds(recordIds);
  const requestedConcurrency = Math.floor(concurrency);
  const batchSize = Number.isFinite(requestedConcurrency)
    ? Math.max(1, requestedConcurrency)
    : RECORD_ACTIVITY_LATEST_CONCURRENCY;
  const byRecordId: Record<string, LatestActivityValue> = {};
  const failedRecordIds: string[] = [];

  throwIfAborted(signal);
  for (let start = 0; start < normalizedRecordIds.length; start += batchSize) {
    const batch = normalizedRecordIds.slice(start, start + batchSize);
    const results = await Promise.allSettled(
      batch.map((recordId) => loadLatest(sb, entityType, recordId, signal, limit)),
    );
    // A superseded list must not cache the partial result that happened to
    // finish before Query cancelled its shared AbortSignal.
    throwIfAborted(signal);
    for (let index = 0; index < results.length; index += 1) {
      const result = results[index];
      const recordId = batch[index];
      if (!recordId) continue;
      if (result.status === 'fulfilled') {
        byRecordId[recordId] = result.value;
      } else {
        failedRecordIds.push(recordId);
      }
    }
  }

  return {byRecordId, failedRecordIds};
}

/**
 * Collapse the shared list Query into the list-presenter contract.
 *
 * A failed record deliberately omits that record's old Query value. The
 * previous effect replaced its local map only after the whole fanout settled,
 * and a failed row then sorted last; retaining a stale value here would change
 * that operator-visible behavior.
 */
export function recordActivityLatestProjection(
  recordIds: readonly string[],
  query: LatestActivityQueryLike,
): RecordActivityLatestProjection {
  const byRecordId: Record<string, LatestActivityValue> = {};
  const complete = !query.isPending && !query.isFetching;
  if (query.data !== undefined) {
    const requested = new Set(recordIds);
    const failedRecordIds = [...new Set(query.data.failedRecordIds.filter((recordId) => requested.has(recordId)))];
    for (const recordId of recordIds) {
      if (Object.hasOwn(query.data.byRecordId, recordId)) {
        byRecordId[recordId] = query.data.byRecordId[recordId];
      }
    }
    return {
      byRecordId,
      recordIds,
      recordListKey: recordIds.join('|'),
      settled: complete,
      loading: false,
      failedRecordIds,
      error: query.error,
      settlementKey: JSON.stringify([
        'data',
        byRecordId,
        failedRecordIds,
        query.dataUpdatedAt || 0,
        query.error ? String(query.error) : null,
        query.errorUpdatedAt || 0,
        query.isPending,
        query.isFetching,
      ]),
    };
  }

  const failedRecordIds = query.error && complete ? [...recordIds] : [];
  return {
    byRecordId,
    recordIds,
    recordListKey: recordIds.join('|'),
    settled: complete && Boolean(query.error),
    loading: !query.error && !complete,
    failedRecordIds,
    error: query.error,
    settlementKey: JSON.stringify([
      query.error ? 'error' : 'pending',
      query.error ? String(query.error) : null,
      query.dataUpdatedAt || 0,
      query.errorUpdatedAt || 0,
      query.isPending,
      query.isFetching,
    ]),
  };
}

type RecordActivityLatestInput = {
  sb: SupabaseClient;
  authState: AuthState;
  rolePreview?: unknown;
  entityType: string;
  recordIds: readonly unknown[] | null | undefined;
  enabled: boolean;
  /** The owning list payload: a new value deliberately refreshes current IDs. */
  sourceVersion: unknown;
  limit?: number;
};

/**
 * Component-scoped owner for an optional list's latest-record Activity fanout.
 * The key is the exact normalized list, so a duplicate mount shares its whole
 * bounded fanout while a list payload advance deliberately refreshes all
 * current record IDs.
 */
export function useRecordActivityLatest({
  sb,
  authState,
  rolePreview,
  entityType,
  recordIds,
  enabled,
  sourceVersion,
  limit = RECORD_ACTIVITY_LATEST_LIMIT,
}: RecordActivityLatestInput): RecordActivityLatestProjection {
  const scope = useMemo(() => recordActivityLatestScope(authState, rolePreview), [authState, rolePreview]);
  const normalizedRecordIds = useMemo(() => collectRecordActivityLatestIds(recordIds), [recordIds]);
  const recordListKey = normalizedRecordIds.join('|');
  const active = Boolean(enabled && scope && entityType && normalizedRecordIds.length > 0);

  const queryKey = useMemo(
    () =>
      active && scope
        ? recordActivityLatestQueryKey(scope, {entityType, recordIds: normalizedRecordIds, limit})
        : UNRESOLVED_RESOURCE_KEY,
    [active, scope, entityType, normalizedRecordIds, limit],
  );
  const query = useQuery<RecordActivityLatestLoad>({
    queryKey,
    // No shared unresolved/public key exists for private audit history. While
    // auth settles or the column is hidden, there is no runnable Activity read.
    // Reading signal is load-bearing: the generic transport forwards it to the
    // one physical RPC builder so Query can cancel an orphaned whole-list load.
    queryFn: active
      ? ({signal}) => loadRecordActivityLatestList(sb, entityType, normalizedRecordIds, signal, {limit})
      : skipToken,
    enabled: active,
  });
  const {isFetching: isLatestActivityFetching, refetch: refetchLatestActivity} = query;

  // A cattle save/refetch often keeps the same record IDs while adding a new
  // Activity event. A changed list key starts its own load automatically; this
  // effect also refreshes any already-settled current list when its owning
  // payload advances, preserving the former `cattle`-identity dependency
  // without putting an unstable source value into cache identity.
  const refreshRef = useRef<{active: boolean; recordListKey: string; sourceVersion: unknown} | null>(null);
  useEffect(() => {
    const previous = refreshRef.current;
    const refreshExisting = Boolean(active && previous && previous.active && previous.sourceVersion !== sourceVersion);
    refreshRef.current = {active, recordListKey, sourceVersion};
    // A changed record list is already fetching under its new key. Its first
    // load covers every current ID; avoid aborting it merely to start a second
    // identical list fanout.
    if (!refreshExisting || isLatestActivityFetching) return;
    void refetchLatestActivity().catch(() => undefined);
  }, [active, recordListKey, sourceVersion, isLatestActivityFetching, refetchLatestActivity]);

  return recordActivityLatestProjection(normalizedRecordIds, query);
}
